/**
 * TabBar
 *
 * Horizontal tab bar rendered above the grid canvas. Displays one tab
 * per LayoutTab with support for switching, inline rename (double-click),
 * close, and new-tab creation (from scratch or from template).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, FileText, LayoutTemplate } from 'lucide-react';
import type { LayoutTab, WidgetPlacement } from '../../contexts/LayoutContext';
import { LAYOUT_TEMPLATES } from '../../config/widgets/templates';
import styles from './TabBar.module.css';

export interface TabBarProps {
  tabs: LayoutTab[];
  activeTabId: string;
  onSwitchTab: (tabId: string) => void;
  onCreateTab: (name: string, template?: WidgetPlacement[]) => void;
  onRenameTab: (tabId: string, name: string) => void;
  onCloseTab: (tabId: string) => void;
}

export const TabBar: React.FC<TabBarProps> = ({
  tabs, activeTabId, onSwitchTab, onCreateTab, onRenameTab, onCloseTab,
}) => {
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const renameInputRef = useRef<HTMLInputElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const canClose = tabs.length > 1;

  const startRename = (tab: LayoutTab) => { setRenamingTabId(tab.id); setRenameValue(tab.name); };

  const commitRename = useCallback(() => {
    if (renamingTabId && renameValue.trim()) onRenameTab(renamingTabId, renameValue.trim());
    setRenamingTabId(null); setRenameValue('');
  }, [renamingTabId, renameValue, onRenameTab]);

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitRename();
    else if (e.key === 'Escape') { setRenamingTabId(null); setRenameValue(''); }
  };

  useEffect(() => {
    if (renamingTabId && renameInputRef.current) { renameInputRef.current.focus(); renameInputRef.current.select(); }
  }, [renamingTabId]);

  const handleStartFromScratch = () => {
    onCreateTab(`Tab ${tabs.length + 1}`);
    setAddMenuOpen(false); setShowTemplates(false);
  };

  const handleSelectTemplate = (template: typeof LAYOUT_TEMPLATES[number]) => {
    const widgets: WidgetPlacement[] = template.widgets.map((tw) => ({
      instanceId: '', widgetType: tw.widgetType, x: tw.x, y: tw.y, w: tw.w, h: tw.h,
    }));
    onCreateTab(template.name, widgets);
    setAddMenuOpen(false); setShowTemplates(false);
  };

  const toggleAddMenu = () => {
    if (!addMenuOpen && addButtonRef.current) {
      const rect = addButtonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
    setAddMenuOpen((prev) => !prev);
    setShowTemplates(false);
  };

  // Close on outside click
  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (addButtonRef.current?.contains(target)) return;
      setAddMenuOpen(false); setShowTemplates(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addMenuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { setAddMenuOpen(false); setShowTemplates(false); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [addMenuOpen]);

  const dropdownMenu = addMenuOpen ? ReactDOM.createPortal(
    <div ref={menuRef} className={styles.addMenu} style={{ top: menuPos.top, left: menuPos.left }}>
      <button className={styles.addMenuItem} onClick={handleStartFromScratch}>
        <span className={styles.addMenuIcon}><FileText size={14} /></span>
        Start from scratch
      </button>
      <button className={styles.addMenuItem} onClick={() => setShowTemplates((p) => !p)}>
        <span className={styles.addMenuIcon}><LayoutTemplate size={14} /></span>
        Start from template
      </button>
      {showTemplates && (
        <div className={styles.templateList}>
          {LAYOUT_TEMPLATES.map((t) => (
            <button key={t.name} className={styles.templateCard} onClick={() => handleSelectTemplate(t)}>
              <span className={styles.templateName}>{t.name}</span>
              <span className={styles.templateDescription}>{t.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  ) : null;

  return (
    <div className={styles.tabBar} role="tablist" aria-label="Layout tabs">
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabId;
        const isRenaming = tab.id === renamingTabId;
        return (
          <div key={tab.id} className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
            role="tab" aria-selected={isActive} aria-label={tab.name}
            onClick={() => { if (!isRenaming) onSwitchTab(tab.id); }}
            onDoubleClick={() => startRename(tab)}>
            {isRenaming ? (
              <input ref={renameInputRef} className={styles.renameInput} value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)} onBlur={commitRename}
                onKeyDown={handleRenameKeyDown} onClick={(e) => e.stopPropagation()}
                aria-label={`Rename tab ${tab.name}`} />
            ) : (
              <span className={styles.tabLabel}>{tab.name}</span>
            )}
            {canClose && !isRenaming && (
              <button className={styles.closeButton}
                onClick={(e) => { e.stopPropagation(); onCloseTab(tab.id); }}
                aria-label={`Close tab ${tab.name}`} title="Close tab">×</button>
            )}
          </div>
        );
      })}
      <button ref={addButtonRef} className={styles.addButton} onClick={toggleAddMenu}
        aria-label="Add new tab" title="Add new tab">
        <Plus size={16} />
      </button>
      {dropdownMenu}
    </div>
  );
};

export default TabBar;
